import { z } from 'zod';

import { EntityId, IsoDate } from '../primitives';

/**
 * What a CMS upload is for (docs/06 §3). `package-file` stays in the enum for the deferred HLS
 * work (docs/07 §4) but nothing in phase 1 produces one.
 */
export const UploadKind = z.enum(['image', 'lrc', 'lyrics', 'source', 'package-file']);
export type UploadKind = z.infer<typeof UploadKind>;

/** Max upload sizes in bytes (docs/06 §3). */
export const UPLOAD_MAX_BYTES: Record<UploadKind, number> = {
  image: 10 * 1024 * 1024,
  lrc: 1024 * 1024,
  lyrics: 1024 * 1024,
  source: 4 * 1024 * 1024 * 1024,
  'package-file': 200 * 1024 * 1024,
};

/**
 * Ceiling of the Worker-proxied fallback: without R2 S3 credentials the bytes travel through
 * the Worker, so only small files (images, lyrics, LRC) can be uploaded at all.
 */
export const UPLOAD_PROXY_MAX_BYTES = 10 * 1024 * 1024;

/** Which entity an `image` upload belongs to (decides the `catalog-images/…` prefix). */
export const UploadOwner = z.enum(['song', 'artist', 'playlist', 'hero']);
export type UploadOwner = z.infer<typeof UploadOwner>;

/** `POST /cms/uploads/sign` */
export const UploadSignBody = z
  .object({
    kind: UploadKind,
    /** Entity the file belongs to; required for everything but a loose image. */
    owner: UploadOwner.optional(),
    ownerId: EntityId.optional(),
    songId: EntityId.optional(),
    filename: z.string().trim().min(1).max(255),
    contentType: z.string().min(1).max(120),
    size: z.number().int().positive(),
  })
  .refine((b) => b.size <= UPLOAD_MAX_BYTES[b.kind], { message: 'File too large', path: ['size'] })
  .refine((b) => b.kind === 'image' || b.songId !== undefined, {
    message: 'songId is required for song assets',
    path: ['songId'],
  })
  .refine((b) => b.kind !== 'image' || b.owner === undefined || b.ownerId !== undefined, {
    message: 'ownerId is required when owner is given',
    path: ['ownerId'],
  });
export type UploadSignBody = z.infer<typeof UploadSignBody>;
export type UploadSignBodyInput = z.input<typeof UploadSignBody>;

/**
 * How the browser must send the bytes:
 * - `presigned`: a direct S3 SigV4 PUT to R2 (`url` is absolute, no auth header).
 * - `proxy`: a PUT to our own Worker (`url` is the API path); the request must carry the
 *   operator's `Authorization` header and the body is capped at
 *   {@link UPLOAD_PROXY_MAX_BYTES}.
 */
export const UploadMode = z.enum(['presigned', 'proxy']);
export type UploadMode = z.infer<typeof UploadMode>;

export const UploadSignResponse = z.object({
  mode: UploadMode,
  /** Absolute R2 URL (`presigned`) or an API path such as `/v1/cms/uploads/proxy?key=…`. */
  url: z.string().min(1),
  method: z.literal('PUT'),
  /** Headers the browser must send with the PUT (at least `Content-Type`). */
  headers: z.record(z.string(), z.string()),
  /** Final R2 key to store on the entity once the PUT succeeds. */
  key: z.string(),
  maxBytes: z.number().int().positive(),
  expiresAt: IsoDate,
});
export type UploadSignResponse = z.infer<typeof UploadSignResponse>;

/** `PUT /cms/uploads/proxy?key=…` — the fallback when the Worker has no R2 S3 credentials. */
export const UploadProxyQuery = z.object({ key: z.string().min(1).max(1024) });
export type UploadProxyQuery = z.infer<typeof UploadProxyQuery>;

export const UploadProxyResponse = z.object({
  key: z.string(),
  size: z.number().int().nonnegative(),
  etag: z.string().nullable(),
});
export type UploadProxyResponse = z.infer<typeof UploadProxyResponse>;
